import type { CallStatus, CallTile, CallMe } from "@bundle/core";
import { Avatar, Icon, IconButton, Pressable, Stack, Text } from "../../primitives/index.ts";
import { useT } from "../../i18n/I18nProvider.tsx";
import { CallControls } from "./CallControls.tsx";

export type HuddleBarProps = {
  title: string;
  isDm: boolean;
  status: CallStatus;
  tiles: CallTile[];
  me: CallMe;
  canShareScreen: boolean;
  /** A moment's note in place of the status line ("Bo declined"). */
  notice?: string;
  onOpen: () => void;
  onMute: () => void;
  onVideo: () => void;
  onScreen: () => void;
  onHand: () => void;
  onSettings: () => void;
  onLeave: () => void;
  testID: string;
};

/** Faces in the bar (more are counted). */
const MAX_FACES = 6;

/** A person's face in the bar, with a ring while they speak and a mic-off badge. */
function Face({ tile, testID }: { tile: CallTile; testID: string }) {
  return (
    <Stack radius="full" padding="0.5" border={tile.speaking ? "status.success" : undefined} testID={testID}>
      <Avatar name={tile.name} src={tile.avatarUrl} square={tile.isAgent} size="avatar.sm" />
    </Stack>
  );
}

/**
 * The active-huddle bar: the conversation, who's in (speaking rings), and the call controls. Web/desktop: the bottom
 * of the sidebar. iOS: `tabViewBottomAccessory`; Android: above the bottom bar.
 */
export function HuddleBar(p: HuddleBarProps) {
  const t = useT();
  const statusText =
    p.notice !== undefined ? p.notice
    : p.status === "joining" ? t("huddle.status.joining")
    : p.status === "connecting" ? t("huddle.status.connecting")
    : p.status === "reconnecting" ? t("huddle.status.reconnecting")
    : p.status === "leaving" ? t("huddle.status.leaving")
    : t("huddle.participants", { count: p.tiles.length });
  const extra = p.tiles.length - MAX_FACES;
  return (
    <Stack gap="2" padding="2.5" radius="lg" background="bg.elevated" border="border.subtle" testID={p.testID}>
      <Pressable direction="horizontal" radius="md" onPress={p.onOpen} accessibilityLabel={t("huddle.openWindow")} testID={`${p.testID}-open`}>
        <Stack grow direction="horizontal" align="center" gap="2">
          <Icon name="huddle" size="icon.sm" color="status.success" />
          <Stack grow gap="0.5">
            <Text variant="footnote" weight="semibold" lines={1}>
              {p.isDm ? p.title : `# ${p.title}`}
            </Text>
            <Text variant="caption" color="text.secondary" lines={1} testID={`${p.testID}-status`}>
              {statusText}
            </Text>
          </Stack>
          <IconButton icon="expand" size="sm" accessibilityLabel={t("huddle.openWindow")} onPress={p.onOpen} testID={`${p.testID}-expand`} />
        </Stack>
      </Pressable>
      {p.tiles.length === 0 ? null : (
        <Stack direction="horizontal" align="center" gap="1" wrap>
          {p.tiles.slice(0, MAX_FACES).map((tile) => (
            <Face key={tile.userId} tile={tile} testID={`${p.testID}-face-${tile.userId}`} />
          ))}
          {extra > 0 ? (
            <Text variant="caption" color="text.secondary">
              {`+${extra}`}
            </Text>
          ) : null}
        </Stack>
      )}
      <CallControls
        me={p.me}
        canShareScreen={p.canShareScreen}
        size="sm"
        onMute={p.onMute}
        onVideo={p.onVideo}
        onScreen={p.onScreen}
        onHand={p.onHand}
        onSettings={p.onSettings}
        onLeave={p.onLeave}
        testID={`${p.testID}-controls`}
      />
    </Stack>
  );
}
